"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.VMRuntimeTemplate = VMRuntimeTemplate;
const opcode_templates_1 = require("./opcode-templates");
const VMExecutorTemplates_1 = require("./VMExecutorTemplates");
const VMRuntimeHelpersTemplate_1 = require("./VMRuntimeHelpersTemplate");
const VMNodeModuleTemplate_1 = require("./VMNodeModuleTemplate");
function VMRuntimeTemplate(options) {
    const { isNodeTarget, splitDispatcher = false, macroOps = false, indirectDispatch = false } = options;
    let DISPATCH_BLOCK;
    if (indirectDispatch) {
        DISPATCH_BLOCK = (0, opcode_templates_1.getIndirectDispatchTemplate)(macroOps);
    }
    else if (splitDispatcher) {
        DISPATCH_BLOCK = (0, opcode_templates_1.getSplitDispatcherCases)(macroOps);
    }
    else {
        DISPATCH_BLOCK = `switch(op) {
                            ${(0, opcode_templates_1.getAllOpcodeCases)(macroOps)}
                            }`;
    }
    const EXCEPTION_HANDLER_LOGIC = (0, VMExecutorTemplates_1.getExceptionHandlerLogicTemplate)();
    const RUNTIME_HELPERS = (0, VMRuntimeHelpersTemplate_1.getRuntimeHelpersTemplate)();
    const NODE_MODULE_VARS_CAPTURE = isNodeTarget ? (0, VMNodeModuleTemplate_1.getNodeModuleVarsCaptureTemplate)() : '';
    return `
        // Global variables storage for top-level const/let declarations
        // Store directly on global object so it's accessible to all VM functions via LOAD_GLOBAL fallback
        // IMPORTANT: Reuse existing vmGlobals if it exists (multiple VM runtimes in same module share it)
        let {vmGlobalsName} = (typeof globalThis !== 'undefined' ? globalThis : (typeof window !== 'undefined' ? window : global))[{vmGlobalsNameString}] || ((typeof globalThis !== 'undefined' ? globalThis : (typeof window !== 'undefined' ? window : global))[{vmGlobalsNameString}] = {});
        ${NODE_MODULE_VARS_CAPTURE}
        // Keep vmModuleVarsName for backward compatibility and explicit checks
        let {vmModuleVarsName} = {vmGlobalsName};

        const {vmExecutorName} = (function() {
            // VM bytecode storage
            let {vmBytecodeArrayName} = {bytecodeArray};

            // Bytecode key mapping (when vmRandomizeKeys is enabled)
            {keyMappingDeclaration}
{binaryDecoderFunctions}
{bytecodeArrayEncodingFunctions}
{bytecodePreprocessing}
            // Opcode mapping (randomized per session)
            let {vmOpcodeMapName} = {opcodeMapping};
${RUNTIME_HELPERS}
{opcodeDerivationFunctions}
{stateMachineFunctions}
            /**
             * Core VM Executor (Generator)
             * Contains opcode switch once, yields at AWAIT/YIELD/YIELD_STAR points.
             * All variables stay in same scope so closures work correctly.
             */
            function* _coreExec(bc, args, parentEnv, currentFunc, newTarget, _this) {
                let stack = [];
                let locals = new Array((bc[{keyP}] || 0) + (bc[{keyL}] || 0));
                let pc = 0;
                let constants = bc[{keyC}];
                let instructions = bc[{keyI}];
                let jumpTargets = bc[{keyJ}] || {};
                let exHandlers = bc[{keyX}] || {};
                let instrCount = instructions.length >> 1;

                let exceptionHandlers = [];
                let pendingException = null;
                let pendingReturn = { hasReturn: false, value: undefined };
                let pendingBreak = { hasBreak: false, target: 0 };
                let pendingContinue = { hasContinue: false, target: 0 };

                // Use per-function opcode map if present (vmOpcodeShuffle), otherwise use global map
                let opMap = bc[{keyO}] || {vmOpcodeMapName};
{opcodeDerivationInit}
{stateMachineInit}
{stackEncodingInit}
                // Jump XOR key for decoding (when vmJumpsEncoding is enabled)
                let jumpKey = bc[{keyJk}] || 0;

                // Bytecode XOR key for decoding (when vmBytecodeEncoding is enabled)
                let bytecodeKey = bc[{keyBk}] || 0;

                // Helper to decode jump target
                let decodeJump = function(encoded) {
                    return jumpKey ? (encoded ^ jumpKey) : encoded;
                };

                // Helper to decode bytecode value
                let decodeBytecode = function(encoded) {
                    return bytecodeKey ? (encoded ^ bytecodeKey) : encoded;
                };

                let env = { parent: parentEnv, vars: {} };

                // Initialize parameters
                if (args) {
                    for (let _i = 0; _i < Math.min(args.length, bc[{keyP}] || 0); _i++) {
                        locals[_i] = args[_i];
                    }
                }

                // Function name binding for named function expressions
                if (bc[{keyNi}] !== undefined && currentFunc) {
                    let funcName = bc[{keyC}][bc[{keyNi}]];
                    env.vars[funcName] = currentFunc;
                    if (!env.constVars) env.constVars = {};
                    env.constVars[funcName] = true;
                    try {
                        Object.defineProperty(currentFunc, 'name', {
                            value: funcName, writable: false, enumerable: false, configurable: true
                        });
                    } catch (e) {}
                }

                // Debug protection check counter (check every 100 instructions)
                let _dbgCheckCounter = 0;

                // Main execution loop - flat array format: [op, arg, op, arg, ...]
                while (pc < instrCount) {
                    try {
                        while (pc < instrCount) {
                            // Debug protection: periodic timing check (when vmDebugProtection is enabled)
                            if (++_dbgCheckCounter > 100) {
                                _dbgCheckCounter = 0;
                                _checkDebug();
                            }

                            let flatIdx = pc << 1;
                            // Decode opcode (XOR with bytecodeKey if enabled)
                            let physicalOp = decodeBytecode(instructions[flatIdx]);
                            // Apply state machine decoding if enabled (physicalOp -> logicalOp)
                            // State is computed from seed and pc (position-based, not history-based)
                            let logicalOp = physicalOp;
                            if (_smStateMaps) {
                                let _smState = _smComputeState(_smSeed, pc);
                                let _smStateMap = _smStateMaps[_smState];
                                if (_smStateMap) {
                                    logicalOp = _smStateMap[physicalOp];
                                }
                            }
                            let op = opMap[logicalOp];
                            // Decode arg (XOR with bytecodeKey if enabled and arg is numeric)
                            let rawArg = instructions[flatIdx + 1];
                            let arg = (rawArg === null) ? undefined : (typeof rawArg === 'number' ? decodeBytecode(rawArg) : rawArg);

                            // AWAIT - yield to async driver
                            if (logicalOp === 122) {
                                let awaitVal = stack.pop();
                                let resolved = yield {t: 1, v: awaitVal};
                                stack.push(resolved);
                                pc++;
                                continue;
                            }

                            // YIELD - yield to generator driver
                            if (logicalOp === 120) {
                                let yieldVal = stack.pop();
                                let input = yield {t: 2, v: yieldVal};
                                stack.push(input);
                                pc++;
                                continue;
                            }

                            // YIELD_STAR - delegate
                            if (logicalOp === 121) {
                                let iterable = stack.pop();
                                let delegateResult = yield {t: 3, v: iterable};
                                stack.push(delegateResult);
                                pc++;
                                continue;
                            }

                            ${DISPATCH_BLOCK}
                        }
                        break;
                    } catch (exception) {
                        ${EXCEPTION_HANDLER_LOGIC}
                    }
                }

                return stack.length > 0 ? stack.pop() : undefined;
            }

            /**
             * Sync VM Executor - drives core generator to completion
             */
            let {vmExecutorName} = function(bytecodeId, args, parentEnv, currentFunc, newTarget) {
                let bc = _getBytecode(bytecodeId);
                let gen = _coreExec(bc, args, parentEnv, currentFunc, newTarget, this);
                let result = gen.next();
                while (!result.done) {
                    // Sync context should never hit await/yield
                    throw new Error('Unexpected async/generator operation in sync context');
                }
                return result.value;
            };

            /**
             * Async VM Executor - drives core generator, awaits at AWAIT signals
             */
            let {vmAsyncExecutorName} = async function(bytecodeId, args, parentEnv, currentFunc, newTarget) {
                let bc = _getBytecode(bytecodeId);
                let gen = _coreExec(bc, args, parentEnv, currentFunc, newTarget, this);
                let result = gen.next();

                while (!result.done) {
                    if (result.value.t === 1) { // AWAIT
                        try {
                            let resolved = await Promise.resolve(result.value.v);
                            result = gen.next(resolved);
                        } catch (e) {
                            result = gen.throw(e);
                        }
                    } else {
                        throw new Error('Unexpected yield in async context');
                    }
                }

                return result.value;
            };


            /**
             * Generator VM Executor - drives core generator, handles YIELD/YIELD_STAR
             */
            let {vmGeneratorExecutorName} = function(bytecodeId, args, parentEnv, currentFunc) {
                let bc = _getBytecode(bytecodeId);
                let gen = _coreExec(bc, args, parentEnv, currentFunc, undefined, this);
                let done = false;
                let currentDelegate = null;
                let genThis = this;

                function step(inputValue, isThrow) {
                    if (done) return { value: undefined, done: true };

                    // Handle active delegate first
                    if (currentDelegate) {
                        let delegateResult;
                        try {
                            delegateResult = isThrow
                                ? (typeof currentDelegate.throw === 'function'
                                    ? currentDelegate.throw(inputValue)
                                    : (currentDelegate = null, (function() { throw inputValue; })()))
                                : currentDelegate.next(inputValue);
                        } catch (e) {
                            currentDelegate = null;
                            // Forward exception to core generator
                            try {
                                let r = gen.throw(e);
                                return handleResult(r);
                            } catch (e2) {
                                done = true;
                                throw e2;
                            }
                        }

                        if (!delegateResult.done) {
                            return { value: delegateResult.value, done: false };
                        }
                        // Delegate exhausted, send return value to core
                        currentDelegate = null;
                        inputValue = delegateResult.value;
                        isThrow = false;
                    }

                    // Drive core generator
                    let result;
                    try {
                        result = isThrow ? gen.throw(inputValue) : gen.next(inputValue);
                    } catch (e) {
                        done = true;
                        throw e;
                    }

                    return handleResult(result);
                }

                function handleResult(result) {
                    if (result.done) {
                        done = true;
                        return { value: result.value, done: true };
                    }

                    let sig = result.value;

                    // YIELD signal (t:2)
                    if (sig.t === 2) {
                        return { value: sig.v, done: false };
                    }

                    // YIELD_STAR signal (t:3) - set up delegation
                    if (sig.t === 3) {
                        let iterable = sig.v;
                        let iter = iterable;
                        if (iter && typeof iter[Symbol.iterator] === 'function') {
                            iter = iter[Symbol.iterator]();
                        }
                        if (iter && typeof iter.next === 'function') {
                            let first = iter.next();
                            if (!first.done) {
                                currentDelegate = iter;
                                return { value: first.value, done: false };
                            }
                            // Delegate immediately done, send return value back
                            return step(first.value, false);
                        }
                        // Invalid iterator
                        return step(undefined, false);
                    }

                    // Unexpected signal
                    throw new Error('Unexpected signal in generator');
                }

                return {
                    next: function(v) { return step(v, false); },
                    return: function(v) {
                        if (done) return { value: v, done: true };
                        done = true;
                        try { gen.return(v); } catch(e) {}
                        return { value: v, done: true };
                    },
                    throw: function(e) {
                        if (done) throw e;
                        return step(e, true);
                    },
                    [Symbol.iterator]: function() { return this; }
                };
            };


            // Return a dispatcher that chooses sync, async, or generator executor based on bytecode flags
            // Must preserve 'this' context for non-arrow functions
            return function(bytecodeId, args, parentEnv, currentFunc, newTarget) {
                let bc = _getBytecode(bytecodeId);
                if (bc && bc[{keyG}]) {
                    // Generator bytecode - create and return generator object (bytecode executes on .next())
                    return {vmGeneratorExecutorName}.call(this, bytecodeId, args, parentEnv, currentFunc);
                } else if (bc && bc[{keyS}]) {
                    // Async bytecode - use async executor
                    return {vmAsyncExecutorName}.call(this, bytecodeId, args, parentEnv, currentFunc, newTarget);
                } else {
                    // Sync bytecode - use sync executor
                    return {vmExecutorName}.call(this, bytecodeId, args, parentEnv, currentFunc, newTarget);
                }
            };
        })();
    `;
}
